import React, { useState } from 'react';
import { Button, DatePicker, Divider, Drawer, Form, Input, Row, Select, Space } from 'antd';
import moment from 'moment';
import { fetchData } from '../../service/dataServices';
import { useTranslation } from 'react-i18next';
import { getLocalizedTitle } from '../../utils/localization';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FormFilterDrawer = ({ drawerVisible, setDrawerVisible, setFilters, filters, formSchema }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [dropdowns, setDropdowns] = useState();
    const [dropdownTypes, setDropdownTypes] = useState();

    fetchData('/api/dropdowns', setDropdowns);
    fetchData('/api/dropdowns/types', setDropdownTypes);

    // Extract filterable fields from form schema
    const filterableFields = formSchema?.fields?.filter(field => field.useForTableFilter) || [];

    const onFinish = (values) => {
        const processedFilters = {};
        Object.keys(values).forEach(key => {
            const value = values[key];
            if (value === undefined || value === null || value === '') {
                return;
            }
            // Handle date range (RangePicker returns an array)
            if (Array.isArray(value) && value.length === 2 && moment.isMoment(value[0])) {
                const fieldId = key.replace('field_', '');
                processedFilters[`${key}_start`] = value[0].format("YYYY-MM-DD");
                processedFilters[`${key}_end`] = value[1].format("YYYY-MM-DD");
            } else {
                processedFilters[key] = value;
            }
        });
        setFilters(prevState => ({
            ...prevState,
            ...processedFilters,
            page: 0,
        }));
    };

    // Resolve dropdown type ID to name, then look up options by name
    const getDropdownOptions = (dropdownTypeId) => {
        if (dropdowns && dropdownTypes && dropdownTypeId) {
            // dropdownTypeId is the ID from /api/dropdowns/types
            // Find the type name from the types list
            const typeObj = dropdownTypes.find(dt => dt.id === dropdownTypeId);
            if (typeObj) {
                // Use the type title (which is the name) to look up in the dropdowns map
                return dropdowns[typeObj.title] || dropdowns[typeObj.id] || [];
            }
            // Fallback: try direct lookup
            return dropdowns[dropdownTypeId] || [];
        }
        return [];
    };

    const getFieldLabel = (field) => {
        if (typeof field.name === 'object') {
            return field.name.en || field.name.ar || `Field ${field.id}`;
        }
        return field.name || `Field ${field.id}`;
    };

    const renderFilterField = (field) => {
        const label = getFieldLabel(field);
        const fieldName = `field_${field.id}`;

        switch (field.type) {
            case 'dropdown':
                return (
                    <Form.Item label={label} name={fieldName} key={field.id}>
                        <Select
                            placeholder={`${t('common.select')} ${label}`}
                            allowClear
                        >
                            {getDropdownOptions(field.dropdownType)?.map(option => (
                                <Option value={option.id} key={option.id}>{getLocalizedTitle(option)}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                );

            case 'boolean':
                return (
                    <Form.Item label={label} name={fieldName} key={field.id}>
                        <Select placeholder={`${t('common.select')} ${label}`} allowClear>
                            <Option value="true">{t('common.yes')}</Option>
                            <Option value="false">{t('common.no')}</Option>
                        </Select>
                    </Form.Item>
                );

            case 'date':
                return (
                    <Form.Item label={label} name={fieldName} key={field.id}>
                        <RangePicker />
                    </Form.Item>
                );

            case 'number':
                return (
                    <Form.Item label={label} name={fieldName} key={field.id}>
                        <Input placeholder={label} allowClear />
                    </Form.Item>
                );

            case 'text':
            case 'long_text':
            default:
                return (
                    <Form.Item label={label} name={fieldName} key={field.id}>
                        <Input placeholder={label} allowClear />
                    </Form.Item>
                );
        }
    };

    const onClear = () => {
        form.resetFields();
        setFilters({
            page: 0,
            size: 10,
        });
    };

    return (
        <Drawer
            title={t('forms.filters')}
            placement="right"
            size="large"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            destroyOnHidden
        >
            <Form
                form={form}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 12 }}
                onFinish={onFinish}
                autoComplete="off"
                initialValues={filters}
            >
                {/* Built-in recordId filter */}
                <Form.Item label={t('forms.recordId')} name="recordId">
                    <Input placeholder={t('forms.recordId')} allowClear />
                </Form.Item>

                <Divider />

                {/* Dynamic filterable fields from schema */}
                {filterableFields.map(field => renderFilterField(field))}

                <Divider />
                <Row justify="center">
                    <Space align="center">
                        <Button type="primary" htmlType="submit">
                            {t('common.search')}
                        </Button>
                        <Button onClick={onClear}>
                            {t('common.clear')}
                        </Button>
                    </Space>
                </Row>
            </Form>
        </Drawer>
    );
};

export default FormFilterDrawer;